import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { environment } from 'src/environments/environment';
import { AppNotification } from './notifications/notification';
import {
  AddArticleDTO,
  AddExerciseDTO,
  AddRecipeDTO,
  GetArticleResponseDTO,
  GetExercisesResponseDTO,
  GetFactsResponseDTO,
  GetRecipeDTO,
  UpdateArticleDTO,
  UpdateExerciseDTO,
  UpdateFactDTO,
  UpdateRecipeDTO,
} from './admin.dto';
import { PhotoService } from '../shared/services/photo.service';

@Injectable({ providedIn: 'root' })
export class AdminService {
  constructor(private http: HttpClient, private photoService: PhotoService) { }
  sendNotification(appNotification: AppNotification) {
    const url = environment.apiUrl + '/api/notification/send';
    return this.http.post<boolean>(url, appNotification);
  }
  addFact(fact: string) {
    const headers = new HttpHeaders({ 'Content-Type': 'application/json' });
    const url = environment.apiUrl + '/api/admin/add/fact';
    return this.http.post<boolean>(url, JSON.stringify(fact), { headers });
  }

  updateFact(updatedFact: UpdateFactDTO) {
    const url = environment.apiUrl + '/api/admin/update/fact';
    return this.http.post<boolean>(url, updatedFact);
  }

  getAllFacts() {
    const url = environment.apiUrl + '/api/admin/facts';
    return this.http.get<GetFactsResponseDTO[]>(url);
  }

  getFactById(factId: number) {
    const url = environment.apiUrl + '/api/admin/fact/' + factId;
    return this.http.get<GetFactsResponseDTO>(url);
  }

  deleteFact(factId: number) {
    const url = environment.apiUrl + '/api/admin/delete/fact/' + factId;
    return this.http.delete<boolean>(url);
  }

  getAllExercises() {
    const url = environment.apiUrl + '/api/admin/exercises';
    return this.http.get<GetExercisesResponseDTO[]>(url);
  }

  getExerciseById(exerciseId: number) {
    const url = environment.apiUrl + '/api/admin/exercise/' + exerciseId;
    return this.http.get<GetExercisesResponseDTO>(url);
  }

  async addExercise(exercise: AddExerciseDTO) {
    const image = exercise.thumbnailImage;
    if (image.startsWith('data:image/jpeg;base64')) {
      await this.photoService
        .uploadImageToFirebase(image, `images/${Date.now()}.png`)
        .then((downloadUrl) => {
          exercise.thumbnailImage = downloadUrl;
        });
    }
    const url = environment.apiUrl + '/api/admin/add/exercise';
    return this.http.post<boolean>(url, exercise);
  }
  deleteExercise(exerciseId: number, imageUrl: string) {
    this.photoService.deleteImageFromFirebase(imageUrl);
    const url = environment.apiUrl + '/api/admin/delete/exercise/' + exerciseId;
    return this.http.delete<boolean>(url);
  }

  async updateExercise(updatedExercise: UpdateExerciseDTO, imageUrl: string) {
    const image = updatedExercise.thumbnailImage;
    if (image.startsWith('data:image/jpeg;base64')) {
      this.photoService.deleteImageFromFirebase(imageUrl);
      await this.photoService
        .uploadImageToFirebase(image, `images/${Date.now()}.png`)
        .then((downloadUrl) => {
          updatedExercise.thumbnailImage = downloadUrl;
        });
    }
    const url = environment.apiUrl + '/api/admin/update/exercise';
    return this.http.post<boolean>(url, updatedExercise);
  }

  getAllArticles() {
    const url = environment.apiUrl + '/api/admin/articles';
    return this.http.get<GetArticleResponseDTO[]>(url);
  }
  getArticleById(articleId: number) {
    const url = environment.apiUrl + '/api/admin/article/' + articleId;
    return this.http.get<GetArticleResponseDTO>(url);
  }
  async updateArticle(updatedArticle: UpdateArticleDTO, imageUrl: string) {
    const image = updatedArticle.image;
    if (image.startsWith('data:image/jpeg;base64')) {
      this.photoService.deleteImageFromFirebase(imageUrl);
      await this.photoService
        .uploadImageToFirebase(image, `images/${Date.now()}.png`)
        .then((downloadUrl) => {
          updatedArticle.image = downloadUrl;
        });
    }
    const url = environment.apiUrl + '/api/admin/update/article';
    return this.http.post<boolean>(url, updatedArticle);
  }
  deleteArticle(articleId: number, imageUrl: string) {
    this.photoService.deleteImageFromFirebase(imageUrl);
    const url = environment.apiUrl + '/api/admin/delete/article/' + articleId;
    return this.http.delete<boolean>(url);
  }
  async addArticle(article: AddArticleDTO) {
    const image = article.image;
    if (image.startsWith('data:image/jpeg;base64')) {
      await this.photoService
        .uploadImageToFirebase(image, `images/${Date.now()}.png`)
        .then((downloadUrl) => {
          article.image = downloadUrl;
        });
    }
    const url = environment.apiUrl + '/api/admin/add/article';
    return this.http.post<boolean>(url, article);
  }
  getAllRecipes() {
    const url = environment.apiUrl + '/api/admin/recipes';
    return this.http.get<GetRecipeDTO[]>(url);
  }

  getRecipeById(recipeId: number) {
    const url = environment.apiUrl + '/api/admin/recipe/' + recipeId;
    return this.http.get<GetRecipeDTO>(url);
  }
  async updateRecipe(updatedRecipe: UpdateRecipeDTO, imageUrl: string) {
    const image = updatedRecipe.image;
    if (image.startsWith('data:image/jpeg;base64')) {
      this.photoService.deleteImageFromFirebase(imageUrl);
      await this.photoService
        .uploadImageToFirebase(image, `images/${Date.now()}.png`)
        .then((downloadUrl) => {
          updatedRecipe.image = downloadUrl;
        });
    }
    const url = environment.apiUrl + '/api/admin/update/recipe';
    return this.http.post<boolean>(url, updatedRecipe);
  }
  deleteRecipe(recipeId: number, imageUrl: string) {
    this.photoService.deleteImageFromFirebase(imageUrl);
    const url = environment.apiUrl + '/api/admin/delete/recipe/' + recipeId;
    return this.http.delete<boolean>(url);
  }
  async addRecipe(recipe: AddRecipeDTO) {
    const image = recipe.image;
    if (image.startsWith('data:image/jpeg;base64')) {
      await this.photoService
        .uploadImageToFirebase(image, `images/${Date.now()}.png`)
        .then((downloadUrl) => {
          recipe.image = downloadUrl;
        });
    }
    const url = environment.apiUrl + '/api/admin/add/recipe';
    return this.http.post<boolean>(url, recipe);
  }
}
